import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { settingsAPI } from '../services/api';
import './SettingsPage.css';

const SettingsPage = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // בדוק שהמשתמש הוא admin
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.role !== 'admin') {
      navigate('/books');
      return;
    }

    loadSettings();
  }, [navigate]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.getAll();
      setSettings(response.data);
      setError('');
    } catch (err) {
      console.error('Error loading settings:', err);
      setError('שגיאה בטעינת ההגדרות');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value: isNaN(value) ? value : parseFloat(value)
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      // הכן את הנתונים לשליחה
      const updates = {};
      Object.entries(settings).forEach(([key, setting]) => {
        updates[key] = setting.value;
      });

      await settingsAPI.updateMultiple(updates);
      setSuccess('ההגדרות שמורות בהצלחה! ✓');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      const errorMsg = err.response?.data?.error || 'שגיאה בשמירת ההגדרות';
      setError(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>טוען הגדרות...</div>;
  }

  if (!settings) {
    return (
      <div style={{ padding: '20px', color: 'red' }}>
        לא הצליח לטעון הגדרות
      </div>
    );
  }

  return (
    <div className="settings-page">
      <h1>⚙️ הגדרות מערכת</h1>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="settings-container">
        {/* הגדרות השאלה */}
        <div className="settings-section">
          <h2>📚 הגדרות השאלה</h2>
          <div className="setting-item">
            <label>ימי השאלה דיפולטיביים</label>
            <input
              type="number"
              value={settings.default_loan_days?.value || 14}
              onChange={(e) => handleChange('default_loan_days', e.target.value)}
              min="1"
              max="365"
              disabled={saving}
            />
            <small>{settings.default_loan_days?.description}</small>
          </div>
        </div>

        {/* הגדרות מנוי */}
        <div className="settings-section">
          <h2>💳 הגדרות מנוי</h2>
          <div className="setting-item">
            <label>גובה דמי המנוי החודשיים (₪)</label>
            <input
              type="number"
        value={settings.subscription_price?.value || 50}       
        onChange={(e) => handleChange('subscription_price', e.target.value)}
              step="1.00"
              min="0"
              disabled={saving}
            />
            <small>{settings.subscription_price?.description}</small>
          </div>
        </div>

        {/* הגדרות קנס */}
        <div className="settings-section">
          <h2>⚠️ הגדרות קנס</h2>
          
          <div className="setting-item">
            <label>תקופת חסימה לפני קנס (ימים)</label>
            <input
              type="number"
              value={settings.fine_grace_period_days?.value || 7}
              onChange={(e) => handleChange('fine_grace_period_days', e.target.value)}
              min="0"
              max="30"
              disabled={saving}
            />
            <small>
              {settings.fine_grace_period_days?.description}
              <br />💡 לדוגמה: אם קבע ל-7, הקנס יתחיל רק לאחר 7 ימים מעיכוב בהחזרה
            </small>
          </div>

          <div className="setting-item">
            <label>גובה קנס בסיסי (₪)</label>
            <input
              type="number"
              value={settings.fine_base_amount?.value || 5}
              onChange={(e) => handleChange('fine_base_amount', e.target.value)}
              step="0.01"
              min="0"
              disabled={saving}
            />
            <small>
              {settings.fine_base_amount?.description}
              <br />💡 זה הקנס ההתחלתי שמשולם לאחר תום תקופת החסימה
            </small>
          </div>

          <div className="setting-item">
            <label>תדירות גדילת קנס (ימים)</label>
            <input
              type="number"
              value={settings.fine_increment_period_days?.value || 1}
              onChange={(e) => handleChange('fine_increment_period_days', e.target.value)}
              min="1"
              max="30"
              disabled={saving}
            />
            <small>
              {settings.fine_increment_period_days?.description}
              <br />💡 לדוגמה: אם קבע ל-1, הקנס יגדל כל יום
            </small>
          </div>

          <div className="setting-item">
            <label>גובה גדילת קנס (₪)</label>
            <input
              type="number"
              value={settings.fine_increment_amount?.value || 2}
              onChange={(e) => handleChange('fine_increment_amount', e.target.value)}
              step="0.01"
              min="0"
              disabled={saving}
            />
            <small>
              {settings.fine_increment_amount?.description}
              <br />💡 לדוגמה: אם קבע ל-2, הקנס יגדל ב-2 שקל בכל תקופה
            </small>
          </div>

          <div className="fine-example">
            <h3>📊 דוגמה לחישוב קנס:</h3>
            <ul>
              <li>תקופת חסימה: 7 ימים ✓ (לא משלם)</li>
              <li>ימים 8-9: {settings.fine_base_amount?.value || 5}₪ (קנס בסיסי)</li>
              <li>ימים 10-11: {((settings.fine_base_amount?.value || 5) + (settings.fine_increment_amount?.value || 2))}₪ (קנס בסיסי + גדילה אחת)</li>
              <li>ימים 12+: והמחשך עולה בהתאם...</li>
            </ul>
          </div>
        </div>

        <div className="settings-actions">
          <button
            className="btn btn-success"
            onClick={handleSave}
            disabled={saving}
            style={{ padding: '12px 24px', fontSize: '16px' }}
          >
            {saving ? 'שומר...' : '💾 שמור הגדרות'}
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => loadSettings()}
            disabled={saving}
            style={{ padding: '12px 24px', fontSize: '16px' }}
          >
            ↻ רענן
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
